import { createSlice } from '@reduxjs/toolkit';
import axios from 'axios';

import {
  addErrorListener,
  addProductUpdatesViewDate as addProductUpdatesViewDateApi,
  getCurrentUser,
  getProductUpdatesViewDate as getProductUpdatesViewDateApi,
  setRequestListener,
} from '../../api/gatewise_api';
import { notifyError, notifySuccess } from '../notifications/notificationsSlice';
import { Role } from '../../models/Role';

const userSlice = createSlice({
  name: 'user',
  initialState: {
    profile: null,
    loading: false,
    pageLoading: true,
    productUpdates: [],
    productInterval: null,
    productUpdatesViewDate: {},
    customToken: '',
    customTokenLoading: false,
  },
  reducers: {
    setUserProfile: (state, action) => {
      state.profile = action.payload;
      state.loading = false;
    },
    setLoading: (state, action) => {
      state.loading = action.payload;
    },
    setPageLoading: (state, action) => {
      state.pageLoading = action.payload;
    },
    setProductUpdates: (state, action) => {
      state.productUpdates = action.payload;
    },
    setProductInterval: (state, action) => {
      state.productInterval = action.payload;
    },
    setProductUpdatesViewDate: (state, action) => {
      state.productUpdatesViewDate = action.payload.date;
    },
    setCustomToken: (state, action) => {
      state.customToken = action.payload;
    },
    setCustomTokenLoading: (state, action) => {
      state.customTokenLoading = action.payload;
    },
  },
});

export const {
  setLoading,
  setUserProfile,
  setPageLoading,
  setProductUpdates,
  setProductUpdatesViewDate,
  setCustomToken,
  setCustomTokenLoading,
} = userSlice.actions;

export default userSlice.reducer;

let isTokenSignInInProgress = false;

// Guard flag: when true, all onAuthStateChanged callbacks and LoginPage
// redirects are suppressed. Prevents the cascade of re-renders and
// competing window.location.href writes that cause the race condition.
let isRedirectingToPortalV3 = false;
export const getIsRedirectingToPortalV3 = () => isRedirectingToPortalV3;

function getPortalV3LoginUrl() {
  return process.env.PORTAL_V3_LOGIN_URL || '';
}

function cleanTokenFromUrl() {
  const url = new URL(window.location.href);
  url.searchParams.delete('token');
  window.history.replaceState({}, document.title, url.toString());
}

function isResetPasswordPage() {
  return window.location.pathname === '/auth/reset-password';
}

/**
 * Intentional logout: clear webadmin Firebase session first, then redirect
 * to portal-v3 with ?signOut=true so it clears its own session too.
 * The isRedirectingToPortalV3 flag prevents the resulting
 * onAuthStateChanged(null) from triggering any cascading dispatches.
 */
export const signOut =
  () =>
  async (dispatch, getState, { authorizer }) => {
    if (!isTokenSignInInProgress && !isResetPasswordPage()) {
      const loginUrl = getPortalV3LoginUrl();
      if (loginUrl) {
        isRedirectingToPortalV3 = true;
        // Clear webadmin Firebase session BEFORE redirect to avoid stale
        // IndexedDB sessions (BUG-2). The flag above prevents the resulting
        // onAuthStateChanged(null) from cascading.
        try {
          await authorizer.signOut();
        } catch (e) {
          // eslint-disable-next-line no-console
          console.error('Failed to clear Firebase session on logout:', e);
        }
        const separator = loginUrl.includes('?') ? '&' : '?';
        window.location.href = `${loginUrl}${separator}signOut=true`;
        return;
      }
    }

    // Fallback for when portal-v3 is not configured (standalone webadmin)
    authorizer.signOut();
    const { productInterval } = getState().user;
    clearInterval(productInterval);
    dispatch(setUserProfile(null));
  };

function signInUser() {
  return async (dispatch) => {
    try {
      dispatch(setPageLoading(true));
      dispatch(setCustomTokenLoading(true));
      const profile = await getCurrentUser();
      dispatch(setCustomToken(profile.customToken));
      dispatch(setUserProfile(profile));
    } catch (e) {
      dispatch(signOut());
      dispatch(notifyError(e.toString()));
    } finally {
      dispatch(setCustomTokenLoading(false));
      dispatch(setPageLoading(false));
    }
  };
}

/**
 * Intentional sign-out (user clicked logout, or ?signOut in URL).
 * Dispatches signOut() which redirects to portal-v3 with ?signOut=true.
 */
function signOutUser() {
  return (dispatch) => {
    dispatch(setPageLoading(true));
    dispatch(signOut());
    dispatch(setPageLoading(false));
  };
}

/**
 * No-session handler: user has no Firebase session and no token in URL.
 * Does NOT redirect to portal-v3 with ?signOut=true — the user isn't
 * logging out, they just need to authenticate. Clears Redux state and
 * lets the route guards show LoginPage, which will redirect to portal-v3
 * with ?returnUrl= so the user comes back after logging in.
 */
function handleNoSession() {
  return (dispatch) => {
    dispatch(setUserProfile(null));
    dispatch(setPageLoading(false));
  };
}

export const login =
  (email, password) =>
  async (dispatch, getState, { authorizer }) => {
    try {
      dispatch(setLoading(true));
      await authorizer.signInWithEmailAndPassword(email, password);
    } catch (err) {
      dispatch(notifyError(err));
    } finally {
      dispatch(setLoading(false));
    }
  };

export const forgotPassword =
  (email) =>
  async (dispatch, getState, { authorizer }) => {
    try {
      dispatch(setLoading(true));
      await authorizer.sendPasswordResetEmail(email);
      dispatch(notifySuccess('Reset Password email was sent'));
    } catch (err) {
      dispatch(notifyError(err));
    } finally {
      dispatch(setLoading(false));
    }
  };

const shouldResendResetPasswordEmail = (err) =>
  err.code === 'auth/expired-action-code' ||
  err.code === 'auth/invalid-action-code' ||
  err.message === 'EXPIRED_OOB_CODE' ||
  err.message === 'INVALID_OOB_CODE';

export const resetPassword =
  (newPassword, code, history, email) =>
  async (dispatch, getState, { authorizer }) => {
    try {
      dispatch(setLoading(true));
      await authorizer.confirmPasswordReset(newPassword, code);
      dispatch(notifySuccess('Password was reset successfully'));
      const portalV3LoginUrl = process.env.PORTAL_V3_LOGIN_URL;
      if (portalV3LoginUrl) {
        window.location.href = portalV3LoginUrl;
      } else {
        history.push('/auth/login');
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(err);
      if (shouldResendResetPasswordEmail(err)) {
        await authorizer.sendPasswordResetEmail(email);
        dispatch(notifyError('Link was expired! - resending a new one now, Please check your inbox.'));
      } else {
        dispatch(notifyError(err.message));
      }
    } finally {
      dispatch(setLoading(false));
    }
  };

export const monitorUserAuthentication = () => (dispatch, getState, deps) => {
  const { authorizer } = deps;

  // BUG-6: Warn when PORTAL_V3_LOGIN_URL is missing — the env var is baked at
  // build time by webpack EnvironmentPlugin. If it's empty, auth redirects
  // to portal-v3 won't work and the user will see the fallback login form.
  if (!getPortalV3LoginUrl()) {
    // eslint-disable-next-line no-console
    console.error(
      '[Gatewise Auth] PORTAL_V3_LOGIN_URL is not configured. ' +
        'WebAdmin requires this variable to redirect to Portal-V3 for authentication. ' +
        'Set it in .env and restart the dev server (webpack bakes env vars at build time).'
    );
  }

  addErrorListener((err) => {
    if (isRedirectingToPortalV3) return;
    // eslint-disable-next-line no-console
    if (!axios.isCancel(err)) console.error(err);
    const s = err?.response?.status;
    // RT-2248: only 401 means the session is invalid. A 403 is a per-resource
    // permission denial (e.g. the ManageApiKeys-gated api-key endpoint the
    // Settings page fetches for everyone) and must NOT sign the user out.
    if (s === 401) {
      dispatch(signOut());
    }
  });

  setRequestListener(async (request) => {
    if (authorizer.isLoggedIn()) {
      const token = await authorizer.getIdToken();
      request.headers = { ...request.headers, Authorization: `Bearer ${token}` };
    }
    return request;
  });

  authorizer.onAuthStateChanged((user) => {
    // If we're already redirecting to portal-v3 (e.g. during signOut),
    // ignore all auth state changes to prevent race conditions.
    if (isRedirectingToPortalV3) return;

    const searchParams = new URLSearchParams(window.location.search);

    if (searchParams.has('signOut')) {
      searchParams.delete('signOut');
      window.history.replaceState({}, document.title, `${window.location.pathname}?${searchParams}`);
      return dispatch(signOutUser());
    }

    const token = searchParams.get('token');
    if (token) {
      cleanTokenFromUrl();
      isTokenSignInInProgress = true;
      authorizer
        .signInWithCustomToken(token)
        .then(() => {
          isTokenSignInInProgress = false;
          dispatch(signInUser());
        })
        .catch((err) => {
          isTokenSignInInProgress = false;
          // eslint-disable-next-line no-console
          console.error('Custom token sign-in failed:', err);
          dispatch(notifyError('Authentication failed. Please try again.'));
          // Redirect to portal-v3 login so user can re-authenticate.
          // Use signOut() which sets the redirect flag and prevents cascading.
          dispatch(signOut());
        });
      return;
    }

    if (user) {
      dispatch(signInUser());
    } else {
      // No Firebase session and no token — user needs to log in.
      // Don't call signOut() (that's for intentional logout with ?signOut=true).
      // Instead, clear state and let route guards show LoginPage,
      // which redirects to portal-v3 with ?returnUrl= for re-entry.
      dispatch(handleNoSession());
    }
  });
};

const permissions = {
  accessPoint: {
    edit: ['superadmin', 'admin', 'manager'],
    editDeviceData: ['superadmin'],
    add: ['superadmin'],
    delete: ['superadmin'],
    reset: ['superadmin'],
    sync: ['superadmin'],
    fetchLogs: ['superadmin'],
  },
  prospectAccess: {
    edit: ['superadmin', 'admin', 'manager'],
    add: ['superadmin', 'admin', 'manager'],
    delete: ['superadmin', 'admin', 'manager'],
  },
  vendorAccess: {
    edit: ['superadmin', 'admin', 'manager'],
    add: ['superadmin', 'admin', 'manager'],
    delete: ['superadmin', 'admin', 'manager'],
  },
  members: {
    addFullAccess: ['superadmin', 'admin'],
  },
  remote: {
    add: ['superadmin'],
    addPincode: ['manager'],
  },
  supportLog: {
    view: ['superadmin'],
  },
  syncLog: {
    view: ['superadmin'],
  },
  groups: {
    addRemoveMembers: [Role.Admin, Role.Worker],
  },
};
export const isAllowedForResourceAndAction = (profile, resource, action, communityId) => {
  if (profile.isAdmin) {
    return true;
  }
  const resourcePermissions = permissions[resource] || {};
  const permittedRoles = resourcePermissions[action] || [];
  const membership = profile.memberships.filter((i) => +i.community_id === +communityId)[0];
  const userRole = membership ? membership.role : null;
  return permittedRoles.includes(userRole);
};

export const isSuperAdmin = (profile) => (profile.roles || []).indexOf('superadmin') >= 0;

export const getProductUpdatesViewDate = () => async (dispatch) => {
  try {
    const { data } = await getProductUpdatesViewDateApi();
    dispatch(setProductUpdatesViewDate(data));
  } catch (err) {
    dispatch(notifyError('Failed to get product update view date'));
  } finally {
    dispatch(setLoading(false));
  }
};

export const addProductUpdatesViewDate = () => async (dispatch) => {
  try {
    await addProductUpdatesViewDateApi();
    dispatch(getProductUpdatesViewDate());
  } catch (err) {
    dispatch(notifyError('Failed to add product update view date'));
  } finally {
    dispatch(setLoading(false));
  }
};
